import axios from 'axios'
import qs from 'qs'
import { sendToAnalytics } from '../Analytics'

/**
 * Node limit from the current page URL (`?maxnodes=`), sent with every
 * graph-recalculation request so the rendered graph honors it. When the
 * URL carries none, the server falls back to the graph's default version,
 * then the account setting, then 150 (lib/maxNodes.js).
 */
const urlMaxNodes = () => {
    if (typeof window === 'undefined') return null
    const raw = new URLSearchParams(window.location.search).get('maxnodes')
    const parsed = parseInt(raw, 10)
    return parsed > 0 ? parsed : null
}

const maxNodesBodyPart = () => {
    const maxNodes = urlMaxNodes()
    return maxNodes ? { maxNodes } : {}
}

// Called once at boot; downstream consumers read the result from
// useContextsStore, not from this function.
export async function fetchOwnerContexts(graphOwnerUsername) {
    const query = graphOwnerUsername
        ? `?owner=${encodeURIComponent(graphOwnerUsername)}`
        : ''
    const response = await fetch(`/react/contexts${query}`)
    return response.json()
}

export const createApiToken = async (userId, name) => {
    const response = await fetch(`/api-token/create`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name,
            userId,
        }),
    })

    const tokenInfo = await response.json()
    if (tokenInfo.error) {
        throw new Error('ERROR creating api token')
    }
    return tokenInfo
}

export const getApiTokens = async userId => {
    const response = await fetch(`/api-token/list`)

    const tokens = await response.json()
    if (tokens.error) {
        throw new Error('ERROR getting api tokens')
    }
    return tokens
}

export const getApiQuotaUsage = async () => {
    const response = await fetch(`/api-token/usage`)
    if (!response.ok) return null
    const data = await response.json()
    if (data.error) return null
    return data
}

export const deleteApiToken = async id => {
    const response = await fetch(`/api-token`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            id,
        }),
    })
    const tokenInfo = await response.json()
    if (tokenInfo.error) {
        throw new Error('ERROR deleting api token')
    }
    return tokenInfo
}

// TODO: do we need to pass those user and context or get it directly from the context?
export const fetchStatements = async (
    username,
    contextName,
    addContextNames,
    isPublic
) => {
    const statementPath = isPublic
        ? '/react/statements/public'
        : '/react/statements'
    const addContextParams =
        addContextNames && addContextNames.length > 0
            ? `?addcontext=${addContextNames.join('&addcontext=')}`
            : ''

    const url = `${statementPath}/${username}/${contextName}${addContextParams}`
    const response = await fetch(url)

    const statementsList = await response.json()
    const statementsWithParsedTags = statementsList.map(({ tags, ...rest }) => {
        return { ...rest, tags: JSON.parse(tags) }
    })

    return statementsWithParsedTags
}
export const fetchPublicStatements = async (
    username,
    contextName,
    addContextNames
) => {
    const addContextParams =
        addContextNames && addContextNames.length > 0
            ? `?addcontext=${addContextNames.join('&addcontext=')}`
            : ''

    const url = `/react/statements/public/${username}/${contextName}${addContextParams}`
    const response = await fetch(url)

    const statementsList = await response.json()
    const statementssWithParsedTags = statementsList.map(
        ({ tags, ...rest }) => {
            return { ...rest, tags: JSON.parse(tags) }
        }
    )

    return statementssWithParsedTags
}

export const fetchSentiment = async (
    statements,
    model,
    user,
    context,
    isPublic
) => {
    const sentimentPath = isPublic ? 'public' : 'user'
    // The server route is `/entries/:scope/sentiment/:type?/:user?/:context?`
    // — `:context?` is optional. Synthesis views have no single context
    // (contextName is null), so omit the segment entirely rather than
    // interpolating it as the literal string "null".
    const basePath = `/entries/${sentimentPath}/sentiment/${model}/${user}`
    const url = context ? `${basePath}/${context}` : basePath
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            statements,
        }),
    })

    const sentiments = await response.json()
    if (sentiments.error) {
        throw new Error('ERROR getting bert sentiment score')
    }
    return sentiments
}

export const fetchPublicSentiment = async (
    statements,
    model,
    user,
    context
) => {
    const sentimentPath = 'public'
    const response = await fetch(
        `/entries/${sentimentPath}/sentiment/${model}/${user}/${context}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                statements,
            }),
        }
    )

    const sentiments = await response.json()
    if (sentiments.error) {
        throw new Error('ERROR getting bert sentiment score')
    }
    return sentiments
}

export const fetchFeatureFlags = async () => {
    const response = await fetch('/react/flags')

    if (!response.ok) {
        throw new Error('Failed to fetch feature flags')
    }

    const flags = await response.json()
    return flags
}

export const toggleExperimentalFeatures = async enabled => {
    const method = enabled ? 'POST' : 'DELETE'
    const response = await fetch('/flags/experimental', {
        method,
        headers: {
            'Content-Type': 'application/json',
        },
    })

    if (!response.ok) {
        throw new Error('Failed to toggle experimental features')
    }

    const data = await response.json()
    return data
}

const apiPaths = {
    renameContext: '/context/rename',
    deleteContext: '/context/delete',
    favoriteContext: '/context/favorite',
    contextSettings: '/context/settings',
    contextVersion: '/context/version',
}

/**
 * Save a graph version (revision URL) for a context. With `isDefault`
 * the backend also marks it as the default revision, so its params
 * (e.g. `maxnodes`) are appended to graph links on every load.
 */
export const saveContextVersion = async (
    contextName,
    { versionName, saveUrl, isDefault }
) => {
    const params = new URLSearchParams({
        context: contextName,
        version_name: versionName,
        saveurl: saveUrl,
    })
    if (isDefault) params.set('version_default', '1')

    const response = await fetch(apiPaths.contextVersion, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
    })
    const data = await response.json()
    if (!response.ok || data.errormsg) {
        throw new Error(
            data.errormsg || `Failed to save graph version: ${response.status}`
        )
    }
    return data
}

/**
 * Toggle the favorite flag on a context. Form-encoded; the backend
 * expects `favorite: '1'|'0'` and `context`.
 */
export const updateFavorite = async (contextName, isFavorite) => {
    const response = await fetch(apiPaths.favoriteContext, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            context: contextName,
            favorite: isFavorite ? '1' : '0',
        }),
    })
    const data = await response.json()
    if (!response.ok || data.errormsg) {
        throw new Error(data.errormsg || `Failed to update favorite: ${response.status}`)
    }
    return data
}

/**
 * Build a settings body that round-trips the full current state.
 *
 * The `/context/settings` endpoint overwrites the whole
 * `textProcessingSettings` object with the fields it receives, so a
 * partial body silently clears stopwords, synonyms, lemmatizeHashtags,
 * etc. To stay safe, this helper serializes the entire legacy
 * `#update_graph_settings` form (which holds every current setting) and
 * applies the caller's `overrides` on top.
 *
 * Throws when the form isn't in the DOM rather than silently sending a
 * destructive partial body. Callers should catch and show a user-facing
 * error.
 *
 * Returns a URLSearchParams suitable for `updateContextSettings(name, body)`.
 */
export const buildContextSettingsBody = (overrides = {}) => {
    const legacyForm = document.getElementById('update_graph_settings')
    if (!legacyForm) {
        throw new Error(
            'Settings form is not loaded — cannot build a safe settings update. Reload the page and try again.'
        )
    }
    const body = new URLSearchParams()
    for (const [key, value] of new FormData(legacyForm).entries()) {
        // Caller passes context separately to updateContextSettings.
        if (key === 'context') continue
        body.append(key, value)
    }
    for (const [key, value] of Object.entries(overrides)) {
        body.set(key, value)
    }
    return body
}

/**
 * Save context language-processing settings (lemmatizer, stopwords, etc.).
 *
 * `body` is either a plain object of field/value pairs OR a pre-built
 * `URLSearchParams` (use `buildContextSettingsBody` to round-trip the full
 * current state — the endpoint overwrites the whole settings object, so
 * a partial body clears anything not sent).
 *
 * `contextName` is set automatically (overrides any `context` key in body).
 */
export const updateContextSettings = async (contextName, body) => {
    const params =
        body instanceof URLSearchParams
            ? body
            : new URLSearchParams(body || {})
    params.set('context', contextName)

    const response = await fetch(apiPaths.contextSettings, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
    })
    const data = await response.json()
    if (!response.ok || data.errormsg) {
        throw new Error(
            data.errormsg ||
                `Failed to update context settings: ${response.status}`
        )
    }
    return data
}

export const deleteContext = async contextName => {
    const body = {
        context: contextName,
    }
    await fetch(apiPaths.deleteContext, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
}

export const renameContext = async (currentName, newName) => {
    const body = {
        newcontext: newName,
        oldcontext: currentName,
    }

    const renameContextResponse = await fetch(apiPaths.renameContext, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
    return renameContextResponse.statusText
}

/**
 * Save a new statement and return the fully recalculated graph in one request.
 *
 * @param {string} text - Statement text
 * @param {Array<string>} tags - Optional tags/categories
 * @param {boolean} extractEntities - Whether to run entity extraction
 * @param {string} contextName - Target context name
 * @returns {Promise<{ uid: number, newEntries: object, entriesAndGraphOfContext: object }>}
 */
export const fetchAddStatementAndGraph = async (
    text,
    tags,
    extractEntities,
    contextName
) => {
    const response = await axios.post(
        '/react/statements/add-and-recalculate',
        { text, tags, contextName, extractEntities, ...maxNodesBodyPart() },
        { headers: { 'Content-Type': 'application/json' } }
    )

    const data = response.data
    if (data.error) {
        throw new Error(data.error)
    }

    return data
}

/** Delete statements and return the recalculated graph in one request. */
export const deleteStatementsAndRecalculate = async (
    statementIds,
    contextName
) => {
    const response = await axios.post(
        '/react/statements/delete-and-recalculate',
        { statementIds, contextName, ...maxNodesBodyPart() },
        { headers: { 'Content-Type': 'application/json' } }
    )
    if (response.data.error) throw new Error(response.data.error)
    return response.data // { removedIds, entriesAndGraphOfContext }
}

/**
 * Edit a statement and return the recalculated graph in one request.
 * Edit is delete-old + add-new server-side, so the returned `uid` is NEW.
 */
export const editStatementAndRecalculate = async (
    statementId,
    text,
    tags,
    contextName,
    extractEntities
) => {
    const response = await axios.post(
        '/react/statements/edit-and-recalculate',
        {
            statementId,
            text,
            tags,
            contextName,
            extractEntities,
            ...maxNodesBodyPart(),
        },
        { headers: { 'Content-Type': 'application/json' } }
    )
    if (response.data.error) throw new Error(response.data.error)
    return response.data // { uid, newEntries, entriesAndGraphOfContext }
}

export const fetchProjectNotes = async context => {
    const response = await fetch(
        `/context/description?context=${encodeURIComponent(context)}`
    )
    if (!response.ok) {
        throw new Error(`Failed to fetch project notes: ${response.status}`)
    }
    const data = await response.json()
    return data.description || ''
}

export const saveProjectNotes = async (context, favorite, graphInfo) => {
    const response = await fetch('/context/description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            context,
            favorite: favorite || '',
            graph_info: graphInfo,
        }),
    })
    const data = await response.json()
    if (!response.ok) {
        throw new Error(`Failed to save project notes: ${response.status}`)
    }
    if (data.errormsg) throw new Error(data.errormsg)
    return data
}

/**
 * Fetch AI topic names for a recalculated graph
 *
 * @param {string|null} contextName - The context name (null when fetching for a synthesis view)
 * @param {Array<number|string>} filteredStatementIds - Array of statement IDs to include
 * @param {Array<string>} stopWords - Optional array of stop words to exclude
 * @param {string|null} userName - Optional username for public graphs
 * @param {'contexts'|'concepts'|null} synthesisType - When set, request topics for that synthesis view
 * @param {Array<string>|null} searchTerms - When set, request topics for a search view
 * @param {Array<string>} additionalContexts - Additional context names for compare mode
 * @param {string|null} comparisonMode - Compare mode ('difference', 'merge', 'intersection')
 * @returns {Promise<Object>} - { topicsExtension, filterMetadata, warning }
 */
export const fetchRecalculatedGraphTopics = async (
    contextName,
    filteredStatementIds = [],
    stopWords = [],
    userName = null,
    synthesisType = null,
    searchTerms = null,
    additionalContexts = [],
    comparisonMode = null
) => {
    const url = '/react/graph/recalculate/topics'

    const isSearch = Array.isArray(searchTerms) && searchTerms.length > 0
    const isCompare = Array.isArray(additionalContexts) && additionalContexts.length > 0

    const body = {
        contextName: isSearch || synthesisType ? null : contextName,
        filteredStatementIds,
        stopWords,
        ...(userName ? { userName } : {}),
        ...(synthesisType && !isSearch ? { synthesisType } : {}),
        ...(isSearch ? { searchTerms } : {}),
        ...(isCompare ? { addContextNames: additionalContexts } : {}),
        ...(isCompare && comparisonMode ? { comparisonMode } : {}),
        ...(!isSearch && !synthesisType ? maxNodesBodyPart() : {}),
    }

    try {
        const response = await axios.post(url, body, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const data = response.data

        if (data.error) {
            throw new Error(data.error)
        }

        return data
    } catch (error) {
        console.error('Error fetching recalculated graph topics:', error)
        throw new Error(
            error.response?.data?.error ||
                error.message ||
                'Failed to fetch recalculated graph topics'
        )
    }
}

/**
 * Fetch synthesis graph (Top Graphs or Top Concepts).
 * Uses the same /react/graph/recalculate endpoint as normal context views,
 * with synthesisType instead of contextName.
 *
 * @param {'contexts' | 'concepts'} synthesisType - Type of synthesis
 * @param {Array<string>} stopWords - Optional stop words
 * @param {string|null} userName - Required for anonymous (public) callers:
 *     the server-side contextsList middleware's anonymous branch uses
 *     `req.body.userName` to resolve the graph owner (see
 *     `lib/middleware/contextsList.js`). Pass the public profile owner's
 *     username when `isPublic`; pass null otherwise.
 * @returns {Promise<Object>} - { entriesAndGraphOfContext }
 */
export const fetchSynthesisGraph = async (
    synthesisType,
    stopWords = [],
    userName = null
) => {
    const url = '/react/graph/recalculate'

    const body = {
        synthesisType,
        filteredStatementIds: [],
        stopWords,
        ...(userName ? { userName } : {}),
    }

    try {
        const response = await axios.post(url, body, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const data = response.data

        if (data.error) {
            throw new Error(data.error)
        }

        return data
    } catch (error) {
        console.error('Error fetching synthesis graph:', error)
        throw new Error(
            error.response?.data?.error ||
                error.message ||
                'Failed to fetch synthesis graph'
        )
    }
}

/**
 * Quiet on failure — topics are an enhancement, never block graph render.
 */
export const fetchSearchGraphTopics = async (
    searchTerms,
    { filteredStatementIds = [], stopWords = [] } = {}
) => {
    if (!Array.isArray(searchTerms) || searchTerms.length === 0) return null
    try {
        const topicsResponse = await fetchRecalculatedGraphTopics(
            null,
            filteredStatementIds,
            stopWords,
            null,
            null,
            searchTerms
        )
        return topicsResponse?.topicsExtension || null
    } catch (topicsError) {
        console.warn(
            '[Search] AI topics fetch failed:',
            topicsError?.message || topicsError
        )
        return null
    }
}

export const fetchSearchGraph = async (searchTerms, { signal } = {}) => {
    if (!Array.isArray(searchTerms) || searchTerms.length === 0) {
        throw new Error(
            'fetchSearchGraph requires a non-empty searchTerms array'
        )
    }

    const url = '/react/graph/recalculate'
    const body = {
        searchTerms,
        filteredStatementIds: [],
        stopWords: [],
    }

    // Pre-request emit matches the legacy submitFromSearch path so the
    // event captures intent regardless of network outcome.
    sendToAnalytics({
        event: 'search',
        data: {
            terms: searchTerms,
            termCount: searchTerms.length,
            source: 'find_page',
        },
    })

    try {
        const response = await axios.post(url, body, {
            headers: { 'Content-Type': 'application/json' },
            signal,
        })

        const data = response.data
        if (data.error) throw new Error(data.error)
        return data
    } catch (error) {
        if (axios.isCancel?.(error) || error?.name === 'CanceledError') {
            throw error
        }
        console.error('Error fetching search graph:', error)
        throw new Error(
            error.response?.data?.error ||
                error.message ||
                'Failed to fetch search graph'
        )
    }
}

/**
 * Fetch AI topic names for a synthesis graph.
 * Reuses the /react/graph/recalculate/topics endpoint with synthesisType.
 *
 * @param {'contexts' | 'concepts'} synthesisType
 * @param {Array<string>} stopWords
 * @param {string|null} userName - See fetchSynthesisGraph — required for
 *     anonymous callers so the contextsList middleware can resolve the owner.
 * @returns {Promise<Object>} - { topicsExtension }
 */
export const fetchSynthesisGraphTopics = async (
    synthesisType,
    stopWords = [],
    userName = null
) => {
    const url = '/react/graph/recalculate/topics'

    const body = {
        synthesisType,
        filteredStatementIds: [],
        stopWords,
        ...(userName ? { userName } : {}),
    }

    try {
        const response = await axios.post(url, body, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const data = response.data

        if (data.error) {
            throw new Error(data.error)
        }

        return data?.topicsExtension || null
    } catch (error) {
        console.error('Error fetching synthesis graph topics:', error)
        return null
    }
}

/**
 * Fetch recalculated graph data and AI topics as two separate steps.
 * Returns graph data immediately and a promise for topics extension.
 *
 * @param {string|null} contextName - Context name (null for synthesis views)
 * @param {Array} filteredStatementIds
 * @param {Array} stopWords
 * @param {string|null} userName
 * @param {'contexts'|'concepts'|null} synthesisType - When set, recalculate the synthesis view
 * @param {Array<string>|null} searchTerms - When set, recalculate a search view
 * @param {Array<string>} additionalContexts - Additional context names for compare mode
 * @param {string|null} comparisonMode - Compare mode ('difference', 'merge', 'intersection')
 */
export const fetchRecalculatedGraphSplit = async (
    contextName,
    filteredStatementIds = [],
    stopWords = [],
    userName = null,
    synthesisType = null,
    searchTerms = null,
    additionalContexts = [],
    comparisonMode = null
) => {
    const graphData = await fetchRecalculatedGraph(
        contextName,
        filteredStatementIds,
        stopWords,
        userName,
        synthesisType,
        searchTerms,
        additionalContexts,
        comparisonMode
    )

    const topicsPromise = fetchRecalculatedGraphTopics(
        contextName,
        filteredStatementIds,
        stopWords,
        userName,
        synthesisType,
        searchTerms,
        additionalContexts,
        comparisonMode
    )
        .then(data => data?.topicsExtension || null)
        .catch(error => {
            console.error('Error fetching AI topics extension:', error)
            return null
        })

    return { graphData, topicsPromise }
}

export const deleteStatements = async (context, statementIds) => {
    const dataToSend = {
        entry: { body: '' },
        name: context,
        delete: 'delete',
        statementid: statementIds,
    }

    const dataAsForm = qs.stringify(dataToSend)

    const data = await axios
        .post('/post', dataAsForm, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        })
        .then(response => {
            const data = response.data

            if (data.statementid) {
                return data
            } else if (data.errormsg) {
                alert(data.errormsg)
            } else {
                alert('Something went wrong, please, try again...')
            }
        })
        .catch(error => {
            console.error('Delete statement error:', error)
            alert(`Server Error: ${error.message}`)
        })
    return data
}

export const editCommonCategories = async (
    statementIds,
    categoriesToAdd,
    categoriesToRemove
) => {
    const body = {
        ids: statementIds,
        categoriesToAdd,
        categoriesToRemove,
    }

    const response = await axios.patch(
        '/react/statements/edit-bulk-categories',
        body,
        {
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )

    return response.data
}

export const editStatement = async (
    text,
    tags,
    statementId,
    contextName,
    contextId
) => {
    const dataToSend = {
        entry: { body: text },
        addedCategories: tags,
        name: contextName,
        edit: 'edit',
        statementid: statementId,
    }

    const dataAsForm = qs.stringify(dataToSend)

    const response = await axios
        .post('/post', dataAsForm, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        })
        .then(response => {
            const data = response.data
            if (!data.entryuid && data.errormsg) {
                alert(data.errormsg)
            }
            return data
        })
        .catch(error => {
            console.error('Edit statement error:', error)
            alert(`Server Error: ${error.message}`)
        })
    let newEditedStatement
    try {
        newEditedStatement = parseStatementResponse(response, contextId)
    } catch (error) {
        console.error('Error parsing statement:', error)
        return
    }
    return newEditedStatement[0]
}
const parseStatementResponse = (statementResponse, contextId) => {
    const uids = JSON.parse(statementResponse.entryuid)
    const parsedStatements = uids.map((uid, index) => {
        const tags = JSON.parse(statementResponse.tags)[index]
        const text = JSON.parse(statementResponse.entrytext)[index]
        const hashtags = JSON.parse(statementResponse.entryname)[
            uid
        ].map(hashtags => hashtags.slice(1))

        const timestamp = JSON.parse(statementResponse.timestamp)[index]
        return {
            uid: parseInt(uid),
            tags,
            text,
            hashtags,
            context: contextId,
            timestamp,
            visible: true,
        }
    })
    return parsedStatements
}

/**
 * Fetch graph and statements data from the API
 * This will replace the old graphFactory.getGraphObject() approach
 */
export const fetchGraphAndStatements = async (
    username,
    contextName,
    addContextNames = [],
    isPublic = false,
    options = {}
) => {
    const {
        includeStatements = true,
        addStats = true,
        includeGraph = true,
        aiTopics = true,
        doNotSave = true,
        stopWords = [],
    } = options

    const url = '/api/v1/graphAndStatements'

    const body = {
        // userName: username,
        name: contextName,
        // addcontext: addContextNames,
        ...(stopWords.length > 0 ? { stopWords } : {}),
    }

    const queryParams = new URLSearchParams({
        includeStatements: 'true',
        addStats: addStats.toString(),
        includeGraph: includeGraph.toString(),
        aiTopics: aiTopics.toString(),
        doNotSave: doNotSave.toString(),
        dotGraph: 'true',
        optimize: 'develop',
    })

    try {
        const response = await axios.post(`${url}?${queryParams}`, body, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const data = response.data

        if (data.error) {
            throw new Error(data.error)
        }

        return data
    } catch (error) {
        console.error('Error fetching graph and statements:', error)
        throw new Error(
            error.response?.data?.error ||
                error.message ||
                'Failed to fetch graph data'
        )
    }
}

/**
 * Fetch recalculated graph from filtered statement IDs
 * Builds a completely new graph with new topics/communities from the filtered statements
 *
 * @param {string|null} contextName - The context name (null when recalculating a synthesis view)
 * @param {Array<number|string>} filteredStatementIds - Array of statement IDs to include
 * @param {Array<string>} stopWords - Optional array of stop words to exclude
 * @param {string|null} userName - Optional username for public graphs
 * @param {'contexts'|'concepts'|null} synthesisType - When set, recalculate that synthesis view
 * @param {Array<string>|null} searchTerms - When set, recalculate a search view
 * @param {Array<string>} additionalContexts - Additional context names for compare mode
 * @param {string|null} comparisonMode - Compare mode ('difference', 'merge', 'intersection')
 * @returns {Promise<Object>} - { entriesAndGraphOfContext, filterMetadata }
 */
export const fetchRecalculatedGraph = async (
    contextName,
    filteredStatementIds = [],
    stopWords = [],
    userName = null,
    synthesisType = null,
    searchTerms = null,
    additionalContexts = [],
    comparisonMode = null
) => {
    const url = '/react/graph/recalculate'

    const isSearch = Array.isArray(searchTerms) && searchTerms.length > 0
    const isCompare = Array.isArray(additionalContexts) && additionalContexts.length > 0

    const body = {
        contextName: isSearch || synthesisType ? null : contextName,
        filteredStatementIds,
        stopWords,
        ...(userName ? { userName } : {}),
        ...(synthesisType && !isSearch ? { synthesisType } : {}),
        ...(isSearch ? { searchTerms } : {}),
        ...(isCompare ? { addContextNames: additionalContexts } : {}),
        ...(isCompare && comparisonMode ? { comparisonMode } : {}),
        ...(!isSearch && !synthesisType ? maxNodesBodyPart() : {}),
    }

    try {
        const response = await axios.post(url, body, {
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const data = response.data

        if (data.error) {
            throw new Error(data.error)
        }

        return data
    } catch (error) {
        console.error('Error fetching recalculated graph:', error)
        const wrapped = new Error(
            error.response?.data?.error ||
                error.message ||
                'Failed to recalculate graph'
        )
        // Typed refusals (QUOTA_GRAPH_LOCKED) keep their code for the bootstrap.
        wrapped.quotaCode = error.response?.data?.code
        throw wrapped
    }
}

/**
 * Transcribe one recorded voice segment (a Blob).
 * Resolves `{ text }` for `mode: 'plain'`, `{ statements, text }` for
 * `mode: 'diarize'` (one statement per speaker turn, each carrying a
 * `speaker: x` category). `speakerReference` is an optional clip of the
 * user's own voice that pins their turns to `speaker: me`.
 */
export const transcribeVoiceAudio = async (
    audioBlob,
    filename,
    contextName,
    {
        mode = 'plain',
        source = 'mic',
        speakerReference = null,
        preview = false,
    } = {}
) => {
    const form = new FormData()
    form.append('audio', audioBlob, filename)
    if (contextName) form.append('contextName', contextName)
    if (speakerReference) {
        form.append('speakerReference', speakerReference, 'me.webm')
    }

    const query = new URLSearchParams({ mode, source })
    if (preview) query.set('preview', '1')
    const response = await axios.post(
        `/react/voice/transcribe?${query}`,
        form
    )

    const data = response.data
    if (data.error) {
        const error = new Error(data.error)
        error.referenceRejected = !!data.referenceRejected
        throw error
    }

    return data
}
